import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Form validation schema
const formSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  businessName: z.string().min(1, 'Business name is required'),
  email: z.string().email('Invalid email address'),
  socialLink: z.string().optional(),
  service: z.string().min(1, 'Please select a service'),
  projectDescription: z.string().min(20, 'Please provide more details about your project'),
  deadline: z.string().min(1, 'Please specify a deadline'),
  budget: z.string().min(1, 'Please enter your budget'),
  inspiration: z.string().optional(),
  extraNotes: z.string().optional(),
});

const services = [
  { value: 'Logo Design or Branding', label: 'Logo Design or Branding' },
  { value: 'Digital Design', label: 'Digital Design' },
  { value: 'Graphic Design', label: 'Graphic Design' },
  { value: 'Web Design & Development', label: 'Web Design & Development' },
  { value: 'Sports Design', label: 'Sports Design' },
];

// Conditional field components
const BrandingFields = ({ register, control }) => (
  <div className="space-y-6 p-6 bg-card/50 rounded-lg border border-border/30">
    <h3 className="font-['Sora'] font-semibold text-foreground">Branding Details</h3>
    
    <div className="space-y-2">
      <Label htmlFor="businessDetails">Tell me about your business and target audience</Label>
      <Textarea
        id="businessDetails"
        {...register('conditionalFields.businessDetails')}
        placeholder="What does your business do? Who are your ideal customers?"
        className="min-h-[100px] bg-input border-border/50"
        data-testid="input-business-details"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="scopeType">What do you need?</Label>
      <Controller
        name="conditionalFields.scopeType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="scope-type-select">
              <SelectValue placeholder="Select scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="logo-only">Logo Only</SelectItem>
              <SelectItem value="full-identity">Full Brand Identity</SelectItem>
              <SelectItem value="brand-refresh">Brand Refresh</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="preferredColors">Preferred Colors</Label>
        <Input
          id="preferredColors"
          {...register('conditionalFields.preferredColors')}
          placeholder="e.g., Blue, Gold, Neutral tones"
          className="bg-input border-border/50"
          data-testid="input-preferred-colors"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="colorsToAvoid">Colors to Avoid</Label>
        <Input
          id="colorsToAvoid"
          {...register('conditionalFields.colorsToAvoid')}
          placeholder="e.g., Pink, Neon colors"
          className="bg-input border-border/50"
          data-testid="input-colors-avoid"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="preferredStyle">Preferred Style</Label>
      <Controller
        name="conditionalFields.preferredStyle"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="style-select">
              <SelectValue placeholder="Select style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="modern-minimal">Modern & Minimal</SelectItem>
              <SelectItem value="bold-playful">Bold & Playful</SelectItem>
              <SelectItem value="classic-elegant">Classic & Elegant</SelectItem>
              <SelectItem value="edgy-dynamic">Edgy & Dynamic</SelectItem>
              <SelectItem value="organic-natural">Organic & Natural</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="logoUsage">Where will the logo be used?</Label>
      <Input
        id="logoUsage"
        {...register('conditionalFields.logoUsage')}
        placeholder="e.g., Website, social media, print materials, signage"
        className="bg-input border-border/50"
        data-testid="input-logo-usage"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="existingLogo">Do you have an existing logo?</Label>
      <Controller
        name="conditionalFields.existingLogo"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="existing-logo-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No, starting fresh</SelectItem>
              <SelectItem value="yes-replace">Yes, but want a complete redesign</SelectItem>
              <SelectItem value="yes-refresh">Yes, want to refresh/update it</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>
  </div>
);

const SocialMediaFields = ({ register, control }) => (
  <div className="space-y-6 p-6 bg-card/50 rounded-lg border border-border/30">
    <h3 className="font-['Sora'] font-semibold text-foreground">Social Media Details</h3>
    
    <div className="space-y-2">
      <Label htmlFor="platforms">Which platforms?</Label>
      <Input
        id="platforms"
        {...register('conditionalFields.platforms')}
        placeholder="e.g., Instagram, TikTok, LinkedIn, Facebook"
        className="bg-input border-border/50"
        data-testid="input-platforms"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="contentType">Type of content needed</Label>
      <Controller
        name="conditionalFields.contentType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="content-type-select">
              <SelectValue placeholder="Select content type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="feed-posts">Feed Posts</SelectItem>
              <SelectItem value="stories">Stories/Reels Covers</SelectItem>
              <SelectItem value="carousels">Carousels</SelectItem>
              <SelectItem value="templates">Reusable Templates</SelectItem>
              <SelectItem value="mixed">Mix of Everything</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="workType">One-time or ongoing?</Label>
      <Controller
        name="conditionalFields.workType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="work-type-select">
              <SelectValue placeholder="Select work type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="one-time">One-time project</SelectItem>
              <SelectItem value="monthly">Monthly retainer</SelectItem>
              <SelectItem value="campaign">Campaign-based</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="hasBranding">Do you already have branding?</Label>
      <Controller
        name="conditionalFields.hasBranding"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="has-branding-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes-full">Yes, complete brand guidelines</SelectItem>
              <SelectItem value="yes-partial">Yes, but limited assets</SelectItem>
              <SelectItem value="no">No branding yet</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="contentGoal">What's the goal of your content?</Label>
      <Input
        id="contentGoal"
        {...register('conditionalFields.contentGoal')}
        placeholder="e.g., Brand awareness, engagement, sales, launches"
        className="bg-input border-border/50"
        data-testid="input-content-goal"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="designCount">How many designs do you need?</Label>
      <Input
        id="designCount"
        {...register('conditionalFields.designCount')}
        placeholder="e.g., 10 posts, 5 stories, etc."
        className="bg-input border-border/50"
        data-testid="input-design-count"
      />
    </div>
  </div>
);

const CreativeFields = ({ register, control }) => (
  <div className="space-y-6 p-6 bg-card/50 rounded-lg border border-border/30">
    <h3 className="font-['Sora'] font-semibold text-foreground">Creative Project Details</h3>
    
    <div className="space-y-2">
      <Label htmlFor="projectType">Type of visual or project</Label>
      <Input
        id="projectType"
        {...register('conditionalFields.projectType')}
        placeholder="e.g., Flyers, banners, presentation, packaging"
        className="bg-input border-border/50"
        data-testid="input-project-type"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="purpose">Purpose of the visual</Label>
      <Input
        id="purpose"
        {...register('conditionalFields.purpose')}
        placeholder="e.g., Event promotion, product launch, marketing campaign"
        className="bg-input border-border/50"
        data-testid="input-purpose"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="existingAssets">Do you have existing assets or branding?</Label>
      <Controller
        name="conditionalFields.existingAssets"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="existing-assets-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes, I'll provide them</SelectItem>
              <SelectItem value="partial">Some, but need more created</SelectItem>
              <SelectItem value="no">No, everything needs to be created</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="deliverables">Required deliverables</Label>
      <Textarea
        id="deliverables"
        {...register('conditionalFields.deliverables')}
        placeholder="List specific files, sizes, or formats needed"
        className="min-h-[80px] bg-input border-border/50"
        data-testid="input-deliverables"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="creativeStyle">Preferred style</Label>
      <Input
        id="creativeStyle"
        {...register('conditionalFields.creativeStyle')}
        placeholder="e.g., Minimalist, vibrant, corporate, artistic"
        className="bg-input border-border/50"
        data-testid="input-creative-style"
      />
    </div>
  </div>
);

const SportsFields = ({ register, control }) => (
  <div className="space-y-6 p-6 bg-card/50 rounded-lg border border-border/30">
    <h3 className="font-['Sora'] font-semibold text-foreground">Sports Design Details</h3>
    
    <div className="space-y-2">
      <Label htmlFor="teamName">Team, club, or athlete name</Label>
      <Input
        id="teamName"
        {...register('conditionalFields.teamName')}
        placeholder="Enter the name"
        className="bg-input border-border/50"
        data-testid="input-team-name"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="designType">Type of design needed</Label>
      <Controller
        name="conditionalFields.designType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="design-type-select">
              <SelectValue placeholder="Select design type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="matchday">Matchday Graphics</SelectItem>
              <SelectItem value="player-spotlight">Player Spotlight</SelectItem>
              <SelectItem value="stat-graphics">Stats & Score Graphics</SelectItem>
              <SelectItem value="announcement">Team Announcement</SelectItem>
              <SelectItem value="merchandise">Merchandise Design</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="hasPhotos">Do you have photos or logos ready?</Label>
      <Controller
        name="conditionalFields.hasPhotos"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="has-photos-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes, I'll provide them</SelectItem>
              <SelectItem value="some">Some, but need more</SelectItem>
              <SelectItem value="no">No, need to source them</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="designPurpose">What is the design for?</Label>
      <Input
        id="designPurpose"
        {...register('conditionalFields.designPurpose')}
        placeholder="e.g., Social media, print, website, broadcast"
        className="bg-input border-border/50"
        data-testid="input-design-purpose"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="sportsWorkType">One-time or ongoing work?</Label>
      <Controller
        name="conditionalFields.sportsWorkType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="sports-work-type-select">
              <SelectValue placeholder="Select work type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="one-time">One-time project</SelectItem>
              <SelectItem value="season">Season-long</SelectItem>
              <SelectItem value="ongoing">Ongoing partnership</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>
  </div>
);

const WebDesignFields = ({ register, control }) => (
  <div className="space-y-6 p-6 bg-card/50 rounded-lg border border-border/30">
    <h3 className="font-['Sora'] font-semibold text-foreground">Web Design & Development Details</h3>
    
    <div className="space-y-2">
      <Label htmlFor="websiteType">Type of website needed</Label>
      <Controller
        name="conditionalFields.websiteType"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="website-type-select">
              <SelectValue placeholder="Select website type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="business">Business Website</SelectItem>
              <SelectItem value="portfolio">Portfolio Website</SelectItem>
              <SelectItem value="landing-page">Landing Page</SelectItem>
              <SelectItem value="ecommerce">E-commerce / Shop</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="websitePurpose">What's the main goal of your website?</Label>
      <Input
        id="websitePurpose"
        {...register('conditionalFields.websitePurpose')}
        placeholder="e.g., Generate leads, showcase work, sell products"
        className="bg-input border-border/50"
        data-testid="input-website-purpose"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="pageCount">Estimated number of pages</Label>
      <Controller
        name="conditionalFields.pageCount"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="page-count-select">
              <SelectValue placeholder="Select page count" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1-3">1-3 pages (Simple)</SelectItem>
              <SelectItem value="4-7">4-7 pages (Standard)</SelectItem>
              <SelectItem value="8-15">8-15 pages (Medium)</SelectItem>
              <SelectItem value="15+">15+ pages (Large)</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="hasWebBranding">Do you have existing branding to incorporate?</Label>
      <Controller
        name="conditionalFields.hasWebBranding"
        control={control}
        render={({ field }) => (
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className="bg-input border-border/50" data-testid="has-web-branding-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes, I have brand guidelines</SelectItem>
              <SelectItem value="partial">Yes, but only logo/colors</SelectItem>
              <SelectItem value="no">No, need branding too</SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="webFeatures">Any specific features needed?</Label>
      <Textarea
        id="webFeatures"
        {...register('conditionalFields.webFeatures')}
        placeholder="e.g., Contact forms, booking system, gallery, blog, animations"
        className="min-h-[80px] bg-input border-border/50"
        data-testid="input-web-features"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="webReferences">Reference websites you like (optional)</Label>
      <Textarea
        id="webReferences"
        {...register('conditionalFields.webReferences')}
        placeholder="Share links to websites you admire or want as inspiration"
        className="min-h-[80px] bg-input border-border/50"
        data-testid="input-web-references"
      />
    </div>
  </div>
);

export const StartProjectForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedService, setSelectedService] = useState('');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      businessName: '',
      email: '',
      socialLink: '',
      service: '',
      projectDescription: '',
      deadline: '',
      budget: '',
      inspiration: '',
      extraNotes: '',
      conditionalFields: {},
    },
  });

const onSubmit = async (data) => {
  setIsSubmitting(true);

  try {
    const formData = new URLSearchParams();

    formData.append('form-name', 'project-inquiry');
    formData.append('fullName', data.fullName);
    formData.append('businessName', data.businessName);
    formData.append('email', data.email);
    formData.append('socialLink', data.socialLink || '');
    formData.append('service', data.service);
    formData.append('projectDescription', data.projectDescription);
    formData.append('deadline', data.deadline);
    formData.append('budget', data.budget);
    formData.append('inspiration', data.inspiration || '');
    formData.append('extraNotes', data.extraNotes || '');
    formData.append('conditionalFields', JSON.stringify(data.conditionalFields || {}));

    await fetch('/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    setSuccessMessage('Your inquiry has been submitted successfully.');
    setIsSuccess(true);
  } catch (error) {
    console.error('Error submitting form:', error);
    alert('There was an error submitting your inquiry. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen flex items-center justify-center px-6"
        data-testid="form-success"
      >
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-['Sora'] text-2xl font-bold text-foreground mb-4">
            Inquiry Submitted!
          </h2>
          <p className="text-muted-foreground mb-8">{successMessage}</p>
          <Link to="/">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8">
              Back to Home
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-8" data-testid="start-project-page">
      <div hidden aria-hidden="true">
  <form name="project-inquiry" data-netlify="true" data-netlify-honeypot="bot-field">
    <input type="text" name="fullName" />
    <input type="text" name="businessName" />
    <input type="email" name="email" />
    <input type="text" name="socialLink" />
    <input type="text" name="service" />
    <textarea name="projectDescription"></textarea>
    <input type="text" name="deadline" />
    <input type="text" name="budget" />
    <textarea name="inspiration"></textarea>
    <textarea name="extraNotes"></textarea>
    <textarea name="conditionalFields"></textarea>
    <input type="text" name="bot-field" />
  </form>
</div>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1
            data-testid="start-project-title"
            className="font-['Sora'] text-3xl sm:text-4xl font-bold text-foreground tracking-tight"
          >
            Start a Project
          </h1>
          <p className="text-muted-foreground mt-4">
            Tell me about your project and I'll get back to you within 24-48 hours with a custom proposal.
          </p>
        </motion.div>

        {/* Form */}
       <motion.form
  name="project-inquiry"
  method="POST"
  data-netlify="true"
  data-netlify-honeypot="bot-field"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.1 }}
  onSubmit={handleSubmit(onSubmit)}
  className="space-y-8"
  data-testid="project-form"
>
  <input type="hidden" name="form-name" value="project-inquiry" />
  <input type="hidden" name="bot-field" />
          {/* Basic Information */}
          <div className="space-y-6">
            <h2 className="font-['Sora'] text-xl font-semibold text-foreground">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  {...register('fullName')}
                  placeholder="Your name"
                  className="bg-input border-border/50"
                  data-testid="input-full-name"
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive">{errors.fullName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessName">Business/Brand Name *</Label>
                <Input
                  id="businessName"
                  {...register('businessName')}
                  placeholder="Your business or brand"
                  className="bg-input border-border/50"
                  data-testid="input-business-name"
                />
                {errors.businessName && (
                  <p className="text-sm text-destructive">{errors.businessName.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="you@example.com"
                  className="bg-input border-border/50"
                  data-testid="input-email"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="socialLink">Instagram/Website (Optional)</Label>
                <Input
                  id="socialLink"
                  {...register('socialLink')}
                  placeholder="@handle or URL"
                  className="bg-input border-border/50"
                  data-testid="input-social-link"
                />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="space-y-6">
            <h2 className="font-['Sora'] text-xl font-semibold text-foreground">
              Project Details
            </h2>

            <div className="space-y-2">
              <Label htmlFor="service">Service Required *</Label>
              <Controller
                name="service"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      setSelectedService(value);
                    }}
                    value={field.value}
                  >
                    <SelectTrigger className="bg-input border-border/50" data-testid="service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.value} value={service.value}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.service && (
                <p className="text-sm text-destructive">{errors.service.message}</p>
              )}
            </div>

            {/* Conditional Fields */}
            <AnimatePresence mode="wait">
              {selectedService === 'Logo Design or Branding' && (
                <motion.div
                  key="branding"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <BrandingFields register={register} control={control} />
                </motion.div>
              )}
              {selectedService === 'Social Media Design' && (
                <motion.div
                  key="social"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <SocialMediaFields register={register} control={control} />
                </motion.div>
              )}
              {selectedService === 'Graphic Design' && (
                <motion.div
                  key="creative"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <CreativeFields register={register} control={control} />
                </motion.div>
              )}
              {selectedService === 'Sports Design' && (
                <motion.div
                  key="sports"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <SportsFields register={register} control={control} />
                </motion.div>
              )}
              {selectedService === 'Web Design & Development' && (
                <motion.div
                  key="webdesign"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <WebDesignFields register={register} control={control} />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label htmlFor="projectDescription">Project Description *</Label>
              <Textarea
                id="projectDescription"
                {...register('projectDescription')}
                placeholder="Describe your project, goals, and what you're looking to achieve..."
                className="min-h-[120px] bg-input border-border/50"
                data-testid="input-project-description"
              />
              {errors.projectDescription && (
                <p className="text-sm text-destructive">{errors.projectDescription.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="deadline">Deadline *</Label>
                <Input
                  id="deadline"
                  {...register('deadline')}
                  placeholder="e.g., 2 weeks, End of March"
                  className="bg-input border-border/50"
                  data-testid="input-deadline"
                />
                {errors.deadline && (
                  <p className="text-sm text-destructive">{errors.deadline.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget">Estimated Budget *</Label>
                <Input
                  id="budget"
                  type="text"
                  {...register('budget')}
                  placeholder="Enter your budget"
                  className="bg-input border-border/50"
                  data-testid="input-budget"
                />
                {errors.budget && (
                  <p className="text-sm text-destructive">{errors.budget.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-6">
            <h2 className="font-['Sora'] text-xl font-semibold text-foreground">
              Additional Information
            </h2>

            <div className="space-y-2">
              <Label htmlFor="inspiration">References/Inspiration Links (Optional)</Label>
              <Textarea
                id="inspiration"
                {...register('inspiration')}
                placeholder="Share links to designs you like or want as reference..."
                className="min-h-[80px] bg-input border-border/50"
                data-testid="input-inspiration"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="extraNotes">Extra Notes (Optional)</Label>
              <Textarea
                id="extraNotes"
                {...register('extraNotes')}
                placeholder="Anything else you'd like to add..."
                className="min-h-[80px] bg-input border-border/50"
                data-testid="input-extra-notes"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-btn"
              className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-12 py-6 text-base btn-shine"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Inquiry'
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              Your information will be sent to depasqualedamian@gmail.com
            </p>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default StartProjectForm;
